import get from 'lodash.get';
import orderBy from 'lodash.orderby';

// Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

import { makeSelectHarvestSchemaEntityTypeId } from 'services/schemas/selectors';
import { selectEntities } from 'services/entities/selectors';
import { getSprayEventVarietiesArray } from 'services/sprayEvents/utils';

// Types
import { AppState } from 'services/types';
import {
    SprayEventsForSubmissionState,
    SprayEventsForSubmissionProps,
    SprayEventForSubmission
} from './types';

const selectSprayEventsForSubmission = (
    state: AppState
): SprayEventsForSubmissionState => state.sprayEventsForSubmission;

const selectSprayEventIdForSubmission = (
    _state: AppState,
    props: SprayEventsForSubmissionProps
): SprayEventsForSubmissionProps['sprayEventId'] => props.sprayEventId;

const selectSprayEventForSubmission = createDeepEqualSelector(
    [selectSprayEventsForSubmission, selectSprayEventIdForSubmission],
    (sprayEvents, sprayEventId): SprayEventForSubmission | undefined =>
        get(sprayEvents, sprayEventId)
);

const selectSprayEventsForSubmissionArray = createDeepEqualSelector(
    selectSprayEventsForSubmission,
    (sprayEvents) => Object.values(sprayEvents)
);

const selectOrderedSprayEventsForSubmission = createDeepEqualSelector(
    selectSprayEventsForSubmissionArray,
    (sprayEvents) => orderBy(sprayEvents, 'startDate')
);

const makeSelectSprayEventForSubmissionStartDate = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) =>
        get(sprayEvent, 'startDate')
    );

const makeSelectSprayEventForSubmissionSprayEventOperations = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) => {
        return get(sprayEvent, ['sprayEvent', 'sprayEventOperations']);
    });

const makeSelectSprayEventForSubmissionApplicationType = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) =>
        get(sprayEvent, ['sprayEvent', 'applicationType'])
    );

const makeSelectSprayEventForSubmissionEntities = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) => {
        return get(sprayEvent, ['sprayEvent', 'sprayEntities']);
    });

const makeSelectSprayEventForSubmissionEntityName = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) =>
        get(sprayEvent, ['entityName'])
    );

const makeSelectSprayEventForSubmissionFinalSpray = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) =>
        get(sprayEvent, ['sprayEvent', 'isFinalSpray'])
    );

const makeSelectSprayEventForSubmissionCropSureDetails = () =>
    createDeepEqualSelector(selectSprayEventForSubmission, (sprayEvent) =>
        get(sprayEvent, ['sprayEvent', 'cropSureDetails'])
    );

const selectSprayEventsForSubmissionVarietyIdsArray = createDeepEqualSelector(
    makeSelectSprayEventForSubmissionEntities(),
    makeSelectHarvestSchemaEntityTypeId(),
    selectEntities,
    (sprayEventEntities, schemaEntityTypeId, entities) =>
        getSprayEventVarietiesArray(
            sprayEventEntities,
            schemaEntityTypeId,
            entities
        )
);

export {
    selectSprayEventsForSubmission,
    selectSprayEventIdForSubmission,
    selectSprayEventForSubmission,
    selectSprayEventsForSubmissionArray,
    selectOrderedSprayEventsForSubmission,
    makeSelectSprayEventForSubmissionStartDate,
    makeSelectSprayEventForSubmissionSprayEventOperations,
    makeSelectSprayEventForSubmissionApplicationType,
    makeSelectSprayEventForSubmissionEntities,
    makeSelectSprayEventForSubmissionEntityName,
    makeSelectSprayEventForSubmissionFinalSpray,
    makeSelectSprayEventForSubmissionCropSureDetails,
    selectSprayEventsForSubmissionVarietyIdsArray
};
